import type { ConversationPlan, IConversationPlanRepository } from '@knownow/domain'
import { ValidationError, VersionMismatchError } from '@knownow/domain'
import { generateShareToken } from '../repository/share-token'

export interface PublishPlanInput {
  readonly planId: string
  readonly expectedVersion: number
}

export interface PublishPlanOutput {
  readonly plan: ConversationPlan
  readonly shareToken: string
}

export class PublishPlanUseCase {
  constructor(
    private readonly repo: IConversationPlanRepository,
    private readonly tokenGenerator: () => string = generateShareToken,
  ) {}

  async execute(input: PublishPlanInput): Promise<PublishPlanOutput> {
    const plan = await this.repo.getById(input.planId)

    if (plan.version !== input.expectedVersion) {
      throw new VersionMismatchError(input.expectedVersion, plan.version)
    }

    if (plan.status === 'active' && plan.shareToken !== null) {
      throw new ValidationError(`Plan is already published`)
    }

    if (plan.status !== 'reviewing' && plan.status !== 'active') {
      throw new ValidationError(
        `Cannot publish plan in '${plan.status}' status; must be 'reviewing' or 'active'`,
      )
    }

    const activeNeeds = plan.informationNeeds.filter((n) => n.deletedAt === null)
    if (activeNeeds.length === 0) {
      throw new ValidationError('Cannot publish plan without any active information needs')
    }

    if (plan.strategy === null) {
      throw new ValidationError('Cannot publish plan without a conversation strategy')
    }
    if (plan.strategy.isStale) {
      throw new ValidationError(
        'Cannot publish plan with a stale strategy; regenerate the strategy first',
      )
    }

    const shareToken = plan.shareToken ?? this.tokenGenerator()
    const published = await this.repo.publish(plan.id, shareToken)

    return { plan: published, shareToken }
  }
}
